/**
 * Cutting a recording into equal parts as it is made — the pure part.
 *
 * MediaRecorder hands over a small Blob every couple of seconds. Object
 * storage wants the file in parts of one fixed size, all equal except the
 * last (R2's rule; S3's minimum is 5 MiB). So the chunks are gathered and
 * sliced at exact byte boundaries: a part may end in the middle of a
 * chunk, and the remainder of that chunk starts the next part. Blob slices
 * share their bytes, so nothing here copies a recording.
 */

export interface PartSlicer {
  /** Feed one chunk; returns every full part that can now be cut, in order. */
  push(chunk: Blob): Blob[]
  /** Whatever is left, as the final (shorter) part; null when nothing is. */
  flush(): Blob | null
  /** Bytes gathered and not yet cut. */
  readonly pending: number
}

export function createPartSlicer(partBytes: number): PartSlicer {
  if (!(partBytes > 0)) throw new Error('partBytes must be positive')
  let held: Blob[] = []
  let heldBytes = 0

  const cut = (): Blob[] => {
    const parts: Blob[] = []
    while (heldBytes >= partBytes) {
      const whole = held.length === 1 ? (held[0] as Blob) : new Blob(held)
      parts.push(whole.slice(0, partBytes))
      const rest = whole.slice(partBytes)
      held = rest.size > 0 ? [rest] : []
      heldBytes = rest.size
    }
    return parts
  }

  return {
    push(chunk) {
      if (chunk.size === 0) return []
      held.push(chunk)
      heldBytes += chunk.size
      return cut()
    },
    flush() {
      if (heldBytes === 0) return null
      const whole = held.length === 1 ? (held[0] as Blob) : new Blob(held)
      held = []
      heldBytes = 0
      return whole
    },
    get pending() {
      return heldBytes
    },
  }
}

/**
 * The MIME type MediaRecorder will accept, best first: VP9 keeps text
 * sharper at the same bitrate, VP8 is everywhere, and a browser that can
 * do neither in WebM (Safari) gets MP4 if it offers it. Null when the
 * browser cannot record at all.
 */
export function pickRecordingMime(
  wanted: 'video' | 'audio',
  supported: (type: string) => boolean = (t) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t),
): string | null {
  const candidates =
    wanted === 'video'
      ? [
          // H.264 first, as the screen recorder does: most machines encode it
          // in hardware, so the recording stays smooth beside the call's own
          // encoders, and the server makes the MP4 by copying it, not
          // re-encoding. VP9 in software at 1080p30 is what made it judder.
          'video/webm;codecs=h264,opus',
          'video/webm;codecs=vp8,opus',
          'video/webm;codecs=vp9,opus',
          'video/webm',
          'video/mp4',
        ]
      : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  return candidates.find((c) => supported(c)) ?? null
}
